import React from "react";
import { FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import BookPicker from "./BookPicker";

// Variables that are auto-filled when a book is selected
const BOOK_VARIABLES = ["book_id", "book_title", "book_author"];

// Variables that are always auto-populated (not user-editable)
const AUTO_POPULATED_VARIABLES = ["name"];

interface NotificationTemplate {
  id: number;
  name: string;
  category: string;
  type: string;
  title_template: string;
  body_template: string;
  action_url: string | null;
  image_url: string | null;
  variables: Record<string, unknown>;
  description: string;
  is_active: boolean;
  created_at: string;
}

interface BookOption {
  id: number;
  title: string;
  authors: string;
  coverImage?: string;
}

interface ContentSelectorProps {
  templates: NotificationTemplate[];
  selectedTemplate: NotificationTemplate | null;
  title: string;
  body: string;
  templateVariables: Record<string, unknown>;
  selectedBook: BookOption | null;
  onTemplateChange: (templateId: string | null) => void;
  onTitleChange: (title: string) => void;
  onBodyChange: (body: string) => void;
  onVariableChange: (key: string, value: string) => void;
  onBookSelect: (book: BookOption | null) => void;
}

/**
 * Check if a variable is actually used in the template title or body.
 * A variable is "required" if it appears as {{variable_name}} in the template text.
 */
function isVariableUsedInTemplate(
  variable: string,
  template: NotificationTemplate | null
): boolean {
  if (!template) return false;
  const pattern = `{{${variable}}}`;
  return (
    (template.title_template || "").includes(pattern) ||
    (template.body_template || "").includes(pattern) ||
    (template.action_url || "").includes(pattern)
  );
}

export default function TemplateSelector({
  templates,
  selectedTemplate,
  title,
  body,
  templateVariables,
  selectedBook,
  onTemplateChange,
  onTitleChange,
  onBodyChange,
  onVariableChange,
  onBookSelect,
}: ContentSelectorProps) {
  // Determine if this template has book-related variables
  const templateVarKeys = selectedTemplate?.variables
    ? Object.keys(selectedTemplate.variables)
    : [];
  const hasBookVariables = BOOK_VARIABLES.some((v) =>
    templateVarKeys.includes(v)
  );

  // All user-editable variables (excluding only auto-populated ones like "name")
  const editableVariables = templateVarKeys.filter(
    (key) => !AUTO_POPULATED_VARIABLES.includes(key)
  );

  // Book-related variables that exist on this template
  const bookVarsOnTemplate = templateVarKeys.filter((key) =>
    BOOK_VARIABLES.includes(key)
  );

  // Check if book selection is required (any book variable is used in template)
  const bookRequired = hasBookVariables && BOOK_VARIABLES.some(
    (v) => templateVarKeys.includes(v) && isVariableUsedInTemplate(v, selectedTemplate)
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" />
          Content
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Template dropdown — optional */}
        <div>
          <Label className="text-xs">Template (optional)</Label>
          <Select
            value={selectedTemplate?.id?.toString() || "none"}
            onValueChange={(val) =>
              onTemplateChange(val === "none" ? null : val)
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="None (write directly)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None (write directly)</SelectItem>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id.toString()}>
                  <div className="flex items-center gap-2">
                    <span>{template.name}</span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      {template.category}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Always-visible Title input */}
        <div>
          <Label className="text-xs">Title</Label>
          <Input
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Notification title"
            className="h-9"
          />
        </div>

        {/* Always-visible Body textarea */}
        <div>
          <Label className="text-xs">Body</Label>
          <Textarea
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            placeholder="Notification body message"
            rows={3}
          />
        </div>

        {/* Template Variables — only when template with variables is selected */}
        {selectedTemplate?.variables &&
          templateVarKeys.length > 0 && (
            <div className="space-y-3">
              <Label className="text-xs">Template Variables</Label>

              {/* Book Picker — quick-fill shortcut for book variables */}
              {hasBookVariables && (
                <div>
                  <BookPicker
                    selectedBook={selectedBook}
                    onSelect={onBookSelect}
                    required={bookRequired}
                  />
                  {selectedBook && bookVarsOnTemplate.length > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Book fields populated below. You can edit them if needed.
                    </p>
                  )}
                </div>
              )}

              {/* Auto-populated name variable info */}
              {templateVarKeys.includes("name") && (
                <div>
                  <Label
                    htmlFor="var-name"
                    className="text-xs text-muted-foreground"
                  >
                    name (auto-populated)
                  </Label>
                  <Input
                    id="var-name"
                    value=""
                    placeholder="Will be auto-populated with user's name"
                    disabled
                    className="h-8 text-sm"
                  />
                </div>
              )}

              {/* All editable variables (book + non-book) */}
              {editableVariables.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {editableVariables.map((key) => {
                    const type = String(selectedTemplate.variables[key]);
                    const isRequired = isVariableUsedInTemplate(
                      key,
                      selectedTemplate
                    );
                    const isBookVar = BOOK_VARIABLES.includes(key);
                    return (
                      <div key={key}>
                        <Label
                          htmlFor={`var-${key}`}
                          className="text-xs text-muted-foreground"
                        >
                          {key} ({type})
                          {isRequired && (
                            <span className="text-destructive ml-0.5">*</span>
                          )}
                          {isBookVar && selectedBook && (
                            <span className="text-blue-500 ml-1">(from book)</span>
                          )}
                        </Label>
                        <Input
                          id={`var-${key}`}
                          value={String(templateVariables[key] || "")}
                          onChange={(e) => onVariableChange(key, e.target.value)}
                          placeholder={`Enter ${key}`}
                          className="h-8 text-sm"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
      </CardContent>
    </Card>
  );
}
